import { formatNumber } from "@/lib/format"

/**
 * Public review figures shown as trust signals: the one source for every
 * rating, review count and check-up count on the site (AHD-14149). Message
 * strings take them as ICU arguments (`{count}`, `{rating}`, `{checkups}`);
 * never type a figure into a message or a component. Pass the check-up count
 * as `formatCheckupCount(country)`: ICU `{checkups, number}` groups digits by
 * the URL language, so Swiss pages would read "2,000" or "2.000".
 *
 * Hardcoded on purpose: the CMS only holds a curated subset of testimonials,
 * but these numbers should match the public profiles (Google Business Profile
 * `ChIJxaSa9ikLkEcRLs6pPJ70NOw`, trustpilot.com/review/aheadhealth.com). Ratings
 * read from the Places API may not be stored under the Maps Platform terms (see
 * `lib/schema.ts`), which is why this is a manual figure.
 *
 * To refresh: read both profiles, update the figures below and bump
 * `REVIEW_STATS_CHECKED_ON`. `scripts/check-review-stats-fresh.ts` warns once
 * the date is older than 60 days.
 */

/** The day every figure below was last read off the live profiles. */
export const REVIEW_STATS_CHECKED_ON = "2026-10-15"

/** Google rating, as displayed (one decimal). */
export const GOOGLE_RATING = "5.0"

/** Google review count. */
export const GOOGLE_REVIEW_COUNT = 49

/** Trustpilot TrustScore, as displayed (one decimal). Every review is 5
 *  stars; Trustpilot's score weighs in a prior for small review counts, so it
 *  reads lower. Show this, never five full stars, wherever Trustpilot stars
 *  appear. */
export const TRUSTPILOT_RATING = "4.4"

/** Trustpilot review count. */
export const TRUSTPILOT_REVIEW_COUNT = 10

/** Share of Google + Trustpilot reviews that are 5 stars, in percent. */
export const FIVE_STAR_SHARE_PERCENT = 100

/** Completed check-ups, as the floor shown in "2000+ check-ups". */
export const CHECKUP_COUNT = 2000

/** `CHECKUP_COUNT` grouped the way `country`'s market writes numbers
 *  ("2'000" in CH, "2.000" in DE, AT and NL). */
export function formatCheckupCount(country: string): string {
  return formatNumber(CHECKUP_COUNT, country)
}
